import { useSelector } from "@tanstack/react-store";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import { VaporUploader } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Divider, Stack } from "@vapor/react-material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetDelegatedMutation } from "../../../../../core/api/use-get-delegated/useGetDelegatedMutation";
import type {
    Intermediary,
    MinimalFulfilment,
} from "../../../../../core/api/use-parse-tax-submission/ParsedTaxSubmissionDTO";
import { useParseTaxSubmission } from "../../../../../core/api/use-parse-tax-submission/useParseTaxSubmission";
import { store } from "../../../../../store/store";
import { TaxSubmissionUploadForm } from "../tax-submission-upload-form/TaxSubmissionUploadForm";

enum UploadError {
    FILE_TOO_BIG,
    MULTIPLE_FILES,
    BADFORMAT_FILENAME,
    GENERIC_ERROR,
    NONE,
    // TODO add intermediary not found error
}

const FILE_SIZE_LIMIT = 1024 * 1024 * 5; // 5mb

const toBase64 = (file: File) => {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            // remove data-url declaration
            // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL

            resolve((reader.result as string).split(",")[1]);
        };
        reader.onerror = reject;
    });
};

interface TaxSubmissionUploadProps {
    onClickCancelButton: () => unknown;
    onUploadTaxSubmission: () => unknown;
}

export const TaxSubmissionUpload: React.FC<TaxSubmissionUploadProps> = ({
    onClickCancelButton,
    onUploadTaxSubmission,
}): JSX.Element => {
    const { t } = useTranslation();
    const subscriptions = useSelector(
        store,
        (state) => state.session.subscriptions,
    );

    const [uploadedTaxSubmissionFile, setUploadedTaxSubmissionFile] = useState<{
        content: string;
        delegated: { name: string; taxId: string; recommended: boolean }[];
        file: File;
        fileType: string;
        flow: "ADE" | "SDI" | "TS";
        fulfilments: MinimalFulfilment[];
        intermediary: Intermediary;
    }>();

    const [uploadError, setUploadError] = useState<UploadError>(
        UploadError.NONE,
    );

    // states -> upload form
    const [isTaxSubmissionUploading, setIsTaxSubmissionUploading] =
        useState(false);

    // use cases
    const {
        isPending: isParsingTaxSubmission,
        mutateAsync: parseTaxSubmission,
    } = useParseTaxSubmission();
    const { mutateAsync: fetchDelegates } = useGetDelegatedMutation();

    // functions
    const onFileUpload = async (filesItems: File[]) => {
        setUploadedTaxSubmissionFile(undefined);
        if (filesItems.length > 1) {
            setUploadError(UploadError.MULTIPLE_FILES);
        } else if (filesItems[0].size > FILE_SIZE_LIMIT) {
            setUploadError(UploadError.FILE_TOO_BIG);
        } else if (
            !/^[0-9a-zA-Z_-]([0-9a-zA-Z_.-]{0,25})?$/.test(filesItems[0].name)
        ) {
            setUploadError(UploadError.BADFORMAT_FILENAME);
        } else {
            setUploadError(UploadError.NONE);

            const uploadedFile = {
                file: filesItems[0],
                content: await toBase64(filesItems[0]),
            };

            try {
                const data = await parseTaxSubmission(uploadedFile.content);
                const delegated = await fetchDelegates({
                    intermediaryTaxId: data.intermediary.taxId,
                });

                const minimalFulfilments = data.fulfilments
                    .map(
                        ({ levyId, accrualYear, name }) =>
                            ({
                                levyId,
                                accrualYear,
                                name,
                            }) as MinimalFulfilment,
                    )
                    .concat(
                        data.fulfilmentsFlexibleDeadline.map(
                            ({ levyId, name }) =>
                                ({
                                    levyId,
                                    name,
                                    accrualYear: undefined,
                                }) as MinimalFulfilment,
                        ),
                    );
                setUploadedTaxSubmissionFile({
                    content: uploadedFile.content,
                    delegated: delegated.map((d) => ({
                        name: `${d.firstName} ${d.lastName}`,
                        taxId: d.taxId,
                        recommended: d.recommended || false,
                    })),
                    fileType: data.fileType,
                    file: uploadedFile.file,
                    fulfilments: minimalFulfilments,
                    intermediary: data.intermediary,
                    flow: data.flow,
                });
            } catch {
                setUploadError(UploadError.GENERIC_ERROR);
            }
        }
    };

    const onRemoveFile = () => {
        setUploadedTaxSubmissionFile(undefined);
        setUploadError(UploadError.NONE);
    };

    const isAgencySubscriptionReadOnly = useMemo(() => {
        if (!uploadedTaxSubmissionFile) return true;
        return !subscriptions.some(
            (sub) =>
                sub.isActive &&
                !sub.isReadOnly &&
                sub.serviceId === `TEL-${uploadedTaxSubmissionFile.flow}`,
        );
    }, [subscriptions, uploadedTaxSubmissionFile]);

    const getErrorTranslation = useCallback(
        (uploadError: UploadError) => {
            switch (uploadError) {
                case UploadError.MULTIPLE_FILES:
                    return t(
                        "views.home.components.tax_submission_upload.uploader.errors.multiple_files",
                    );
                case UploadError.FILE_TOO_BIG:
                    return t(
                        "views.home.components.tax_submission_upload.uploader.errors.file_too_big",
                    );
                case UploadError.BADFORMAT_FILENAME:
                    return t(
                        "views.home.components.tax_submission_upload.uploader.errors.badformat_filename",
                    );
                case UploadError.GENERIC_ERROR:
                default:
                    return t(
                        "views.home.components.tax_submission_upload.uploader.errors.generic_error",
                    );
            }
        },
        [t],
    );

    return (
        <Stack spacing={3}>
            <Typography variant="titleMedium">
                {t("views.home.components.tax_submission_upload.upload_file")}
            </Typography>
            <LoadingWrapper
                loading={isTaxSubmissionUploading || isParsingTaxSubmission}
                spinnerProps={{ loadingText: "" }}
                variant={isParsingTaxSubmission ? "spinner" : "backdrop"}
            >
                <VaporUploader
                    buttonLabel={t(
                        "views.home.components.tax_submission_upload.uploader.button_label",
                    )}
                    buttonVariant="outlined"
                    description={t(
                        "views.home.components.tax_submission_upload.uploader.description",
                    )}
                    draggable
                    draggableText={t(
                        "views.home.components.tax_submission_upload.uploader.draggable_text",
                    )}
                    dropText={t(
                        "views.home.components.tax_submission_upload.uploader.drop_text",
                    )}
                    error={
                        uploadError !== UploadError.NONE ||
                        (isAgencySubscriptionReadOnly &&
                            !!uploadedTaxSubmissionFile)
                    }
                    errorMessage={
                        uploadError !== UploadError.NONE
                            ? getErrorTranslation(uploadError)
                            : isAgencySubscriptionReadOnly &&
                                !!uploadedTaxSubmissionFile
                              ? t(
                                    "views.home.components.tax_submission_upload_form.errors.is_agency_subscription_readonly",
                                )
                              : undefined
                    }
                    inputFileProps={{
                        multiple: false,
                    }}
                    onFileUpload={onFileUpload}
                    onRemove={onRemoveFile}
                />
            </LoadingWrapper>
            {!isAgencySubscriptionReadOnly && uploadedTaxSubmissionFile && (
                <>
                    <Divider light />
                    <TaxSubmissionUploadForm
                        delegated={uploadedTaxSubmissionFile.delegated}
                        fileContent={uploadedTaxSubmissionFile.content}
                        fileName={uploadedTaxSubmissionFile.file.name}
                        fileType={uploadedTaxSubmissionFile.fileType}
                        fulfilments={uploadedTaxSubmissionFile.fulfilments}
                        intermediary={uploadedTaxSubmissionFile.intermediary}
                        isTaxSubmissionUploading={(isUploading) =>
                            setIsTaxSubmissionUploading(isUploading)
                        }
                        onClickCancelButton={onClickCancelButton}
                        onUploadTaxSubmission={onUploadTaxSubmission}
                        uploadFlow={uploadedTaxSubmissionFile.flow}
                    />
                </>
            )}
        </Stack>
    );
};
